import {
  Col,
  type Node,
  Op,
  type TransitionsFilterV2,
  type TransitionsV3Filters,
} from '@revelio/data-access';

import {
  type Filter,
  isMultiFilter,
  type PrimaryFilterId,
} from '../../types/filters';
import { isExpressionFilter } from './expression-dimensions';
import { catLeaf, combine, filterOperands } from './filters-expr';

const ATTRIBUTE_FILTERS = [
  ['seniority', Col.Seniority],
  ['highest_degree', Col.HighestDegree],
] as const;

const TEXT_FILTERS = [
  ['title', Col.Title],
  ['description', Col.Keywords],
] as const;

/**
 * Seniority, education, title and keywords as the middleware reads them in
 * `filters`: a position matches any value of a group, and every group.
 */
const attributeOperands = (filters: TransitionsV3Filters): Node[] => {
  const attributes = ATTRIBUTE_FILTERS.map(([key, col]) =>
    combine(
      Op.Or,
      (filters[key] ?? []).map((id) => catLeaf(col, id))
    )
  );
  const texts = TEXT_FILTERS.flatMap(([key, col]) =>
    (filters[key] ?? []).map((terms) =>
      combine(
        Op.Or,
        terms.map((txt) => ({ leaf: { txt: { col, txt } } }))
      )
    )
  );
  return [...attributes, ...texts].filter(
    (node): node is Node => node !== null
  );
};

/**
 * A transitions request's `filters` as its `filters_v2`: the page's entity
 * filters as the user combined them (`buildFiltersExpr`'s semantics), the
 * rest of `filters` as the middleware reads it, and the dates, gender and
 * ethnicity beside the expression.
 *
 * Nothing here says which position a value is matched against: the
 * middleware compiles the expression against the position a person left for
 * outflows and the one they joined for inflows, as it does `filters`.
 */
export const toTransitionsFiltersV2 = ({
  filters,
  primaryDimension,
  effectiveFilters,
}: {
  filters: TransitionsV3Filters;
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
}): TransitionsFilterV2 => {
  const expressed = effectiveFilters.filter(
    (filter) =>
      (isMultiFilter(filter) ? filter.wrappedFilterId : filter.id) !==
        primaryDimension && isExpressionFilter(filter)
  );
  const filtersExpr = combine(Op.And, [
    ...filterOperands(expressed),
    ...attributeOperands(filters),
  ]);

  const { start_date, end_date, gender, ethnicity } = filters;
  return {
    start_date,
    end_date,
    gender,
    ethnicity,
    ...(filtersExpr === null ? {} : { filters_expr: filtersExpr }),
  };
};
